// Reactと関連するフック（useState, useEffectなど）をインポートします。
import React, { useState, useEffect, useCallback, useMemo } from 'react';
// Firebaseのデータベース(db)と認証(auth)機能をインポートします。
import { db, auth } from '../firebase';
// Firestoreからデータを取得・操作するための関数をインポートします。
import { collection, getDocs, doc, getDoc, query, where, deleteDoc } from 'firebase/firestore';
// UIコンポーネントをReact Bootstrapからインポートします。
import { Container, Card, Spinner, Alert, Button, Accordion, Row, Col, Table, Badge, Pagination } from 'react-bootstrap';
// 画面遷移(Link, useNavigate)のための機能をインポートします。
import { Link, useNavigate } from 'react-router-dom';
import { Clipboard } from 'react-bootstrap-icons';

// ★ 追加: ここから「トップへ戻るボタン」のコンポーネント定義
/**
 * ページトップに戻るボタンのコンポーネント
 */
const ScrollToTopButton = () => {
  // ボタンが表示されているかどうかを管理するstate
  const [isVisible, setIsVisible] = useState(false);

  // スクロール位置をチェックして、ボタンの表示/非表示を切り替える関数
  const toggleVisibility = () => {
    // 垂直方向のスクロール量が300pxを超えたら表示
    if (window.pageYOffset > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  // ページトップへスムーズにスクロールさせる関数
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // コンポーネントがマウントされたときにスクロールイベントを追加
  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility);
    // コンポーネントがアンマウントされるときにイベントを削除
    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, []);

  // ボタンのスタイル定義
  const buttonStyle = {
    position: 'fixed',
    bottom: '2rem',
    right: '2rem',
    zIndex: 1000,
    opacity: isVisible ? 1 : 0, // isVisibleの値に応じて透明度を変更
    visibility: isVisible ? 'visible' : 'hidden',
    transition: 'opacity 0.3s, visibility 0.3s',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
  };

  return (
    <Button
      variant="dark"
      onClick={scrollToTop}
      style={buttonStyle}
      aria-label="トップに戻る"
    >
      {/* 上矢印のアイコン (SVG) */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 19V5M5 12l7-7 7 7" />
      </svg>
    </Button>
  );
};
// ★ 追加: ここまで「トップへ戻るボタン」のコンポーネント定義

/**
 * 発注番号を見やすいようにハイフン区切りにフォーマットする関数
 * @param {string} orderNumber - 13桁以上の注文番号 (例: "2023010100100")
 * @returns {string} フォーマットされた注文番号 (例: "20230101-001-00")
 */
const formatDisplayOrderNumber = (orderNumber) => {
  // 注文番号が不正な場合は、そのまま返す
  if (!orderNumber || orderNumber.length < 13) {
    return orderNumber;
  }
  // 注文番号を日付部分、日次連番、配送先連番に分割
  const datePart = orderNumber.substring(0, 8);
  const dailyPart = orderNumber.substring(8, 11);
  const destPart = orderNumber.substring(11);
  // 分割したパーツをハイフンで連結して返す
  return `${datePart}-${dailyPart}-${destPart}`;
};

/**
 * YYYYMMDD形式の日付文字列をYYYY/MM/DD形式に変換する関数
 * @param {string} dateStr - 8桁の日付文字列 (例: "20230101")
 * @returns {string} フォーマットされた日付文字列 (例: "2023/01/01")
 */
const formatShippingDate = (dateStr) => {
  // 日付文字列が不正な場合は、そのまま返す
  if (!dateStr || dateStr.length !== 8) {
    return dateStr;
  }
  // 文字列を年、月、日に分割
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);
  // 分割したパーツをスラッシュで連結して返す
  return `${year}/${month}/${day}`;
};

/**
 * 各注文の「変更」「キャンセル」ボタンとステータス表示を管理するコンポーネント
 * @param {object} props - コンポーネントに渡されるプロパティ
 * @param {object} props.order - 表示する注文データ
 * @param {number} props.orderChangeTimeLimit - 注文変更が可能な制限時間（分）
 * @param {string} props.orderChangeDeadlineTime - 注文変更の絶対的な締切時刻 (HH:mm形式)
 * @param {string} props.deliveryDeadlineTime - 当日配送の締切時刻 (HH:mm形式)
 * @param {function} props.onCancel - キャンセルボタンが押されたときに実行される関数
 */

// function OrderActions({ order, orderChangeTimeLimit, orderChangeDeadlineTime, deliveryDeadlineTime, onCancel }) {
//   const navigate = useNavigate();

//   // 【変更点】ご要望のロジックに基づいて締切日時を計算するように全面的に修正
//   const deadline = useMemo(() => {
//     const orderDate = order.orderedAt?.toDate();
//     if (!orderDate) return null;

//     // 注文日と同じ日付で、各締切時刻のDateオブジェクトを生成
//     const createDeadlineDate = (timeString) => {
//       const d = new Date(orderDate);
//       if (timeString && timeString.includes(':')) {
//         const [hours, minutes] = timeString.split(':');
//         d.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
//       }
//       return d;
//     };

//     const deliveryDeadlineDate = createDeadlineDate(deliveryDeadlineTime);
//     const orderChangeDeadlineDate = createDeadlineDate(orderChangeDeadlineTime);
    
//     // 発注時刻 + 変更可能時間で計算した締切
//     const deadlineFromTimeLimit = new Date(orderDate.getTime() + orderChangeTimeLimit * 60 * 1000);

//     // --- 新しい締切ロジック ---
//     if (orderDate > deliveryDeadlineDate) {
//       // 発注日時が当日配送締切を超えている場合
//       return deadlineFromTimeLimit;
//     } else {
//       // 発注日時が当日配送締切以内の場合
//       if (orderDate > orderChangeDeadlineDate) {
//         // 変更受付締切を過ぎてから、当日配送締切までの間に注文した場合
//         return new Date(0); // 変更不可（過去の時刻を返す）
//       }
//       // 最終的な締切は、「発注+猶予時間」と「変更受付締切」のうち、より早い方
//       return new Date(Math.min(deadlineFromTimeLimit, orderChangeDeadlineDate));
//     }

//   }, [order.orderedAt, orderChangeTimeLimit, orderChangeDeadlineTime, deliveryDeadlineTime]);

//   const calculateTimeLeft = useCallback(() => {
//     if (!deadline) return {};
//     const difference = +deadline - +new Date();
//     return difference > 0 ? {
//       minutes: Math.floor((difference / 1000 / 60) % 60),
//       seconds: Math.floor((difference / 1000) % 60),
//     } : {};
//   }, [deadline]);

//   const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

//   useEffect(() => {
//     if (!deadline) return;
//     const timer = setTimeout(() => {
//       setTimeLeft(calculateTimeLeft());
//     }, 1000);
//     return () => clearTimeout(timer);
//   }, [timeLeft, deadline, calculateTimeLeft]);

//   const isModifiable = Object.keys(timeLeft).length > 0;

//   const handleEditClick = () => {
//     if (isModifiable) {
//       navigate(`/edit-order/${order.id}`);
//     } else {
//       alert('変更可能な期間が終了しています。');
//     }
//   };

//   return (
//     <div className="text-end mt-3">
//       <div className="mb-2">
//         {isModifiable ? (
//           <Badge bg="success">
//             変更可能
//             <small>
//               (残り {String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')})
//             </small>
//           </Badge>
//         ) : (
//           <Badge bg="secondary">
//             期間終了
//           </Badge>
//         )}
//       </div>
//       <Button
//         variant="outline-danger"
//         size="sm"
//         disabled={!isModifiable}
//         onClick={() => onCancel(order.id, order.shippingDestinations[0].orderNumber)}
//       >
//         この注文をキャンセル
//       </Button>
//       <Button
//         variant="primary"
//         size="sm"
//         className="ms-2"
//         disabled={!isModifiable}
//         onClick={handleEditClick}
//       >
//         この注文内容を変更する
//       </Button>
//     </div>
//   );
// }

// --- OrderActions コンポーネントの修正 ---

function OrderActions({ order, onCancel }) { // 使わなくなったpropsは削除してOK
  const navigate = useNavigate();

  // 修正ポイント: 複雑な再計算をやめ、注文データにある modifiableUntil をそのまま使う
  const deadline = useMemo(() => {
    return order.modifiableUntil?.toDate() || null;
  }, [order.modifiableUntil]);

  const calculateTimeLeft = useCallback(() => {
    if (!deadline) return {};
    const difference = +deadline - +new Date();
    return difference > 0 ? {
      minutes: Math.floor((difference / 1000 / 60)), // 60分以上の猶予がある場合も考慮
      seconds: Math.floor((difference / 1000) % 60),
    } : {};
  }, [deadline]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    if (!deadline) return;
    const timer = setInterval(() => { // setTimeoutからsetIntervalに変更して正確に更新
      const left = calculateTimeLeft();
      setTimeLeft(left);
    }, 1000);
    return () => clearInterval(timer);
  }, [deadline, calculateTimeLeft]);

  const isModifiable = Object.keys(timeLeft).length > 0;

  const handleEditClick = () => {
    if (isModifiable) {
      navigate(`/edit-order/${order.id}`);
    } else {
      alert('変更可能な期間が終了しています。');
    }
  };

  return (
    <div className="text-end mt-3">
      <div className="mb-2">
        {isModifiable ? (
          <Badge bg="success">
            変更可能
            <small>
              (残り {String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')})
            </small>
          </Badge>
        ) : (
          <Badge bg="secondary">
            期間終了
          </Badge>
        )}
      </div>
      <Button
        variant="outline-danger"
        size="sm"
        disabled={!isModifiable}
        onClick={() => onCancel(order.id, order.shippingDestinations[0].orderNumber)}
      >
        この注文をキャンセル
      </Button>
      <Button
        variant="primary"
        size="sm"
        className="ms-2"
        disabled={!isModifiable}
        onClick={handleEditClick}
      >
        この注文内容を変更する
      </Button>
    </div>
  );
}

const statusMap = {
  new: { text: '新規', bg: 'primary' },
  processed: { text: '受付済み', bg: 'success' },
  shipped: { text: '出荷準備完了', bg: 'danger' }
};

const getTrackingUrl = (carrier, trackingNumber) => {
  if (!carrier || !trackingNumber) return null;
  const encodedTrackingNumber = encodeURIComponent(trackingNumber);
  switch (carrier) {
    case 'yupri':
      return `https://www.post.japanpost.jp/receive/tracking/result.php?code=${encodedTrackingNumber}`;
    case 'istar':
      return `https://corp.fukutsu.co.jp/situation/tracking_no_search?tracking_no=${encodedTrackingNumber}`;
    default:
      return null;
  }
};

/**
 * 発注履歴全体を表示するメインコンポーネント
 */
function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orderChangeTimeLimit, setOrderChangeTimeLimit] = useState(30);
  const [orderChangeDeadlineTime, setOrderChangeDeadlineTime] = useState('14:45'); 
  // 【変更点】当日配送締切時刻を管理するstateを追加
  const [deliveryDeadlineTime, setDeliveryDeadlineTime] = useState('15:00');
  const [pastOrdersCurrentPage, setPastOrdersCurrentPage] = useState(1);
  const itemsPerPage = 18;

  const fetchData = useCallback(async (userId) => {
    setLoading(true);
    try {
      const systemSettingsRef = doc(db, 'system_settings', 'config');
      const systemSettingsSnap = await getDoc(systemSettingsRef);
      if (systemSettingsSnap.exists()) {
        const settings = systemSettingsSnap.data();
        setOrderChangeTimeLimit(settings.orderChangeTimeLimit || 30);
        setOrderChangeDeadlineTime(settings.orderChangeDeadlineTime || '14:45');
        // 【変更点】Firestoreから当日配送締切時刻を取得
        setDeliveryDeadlineTime(settings.deliveryDeadline || '15:00');
      }
      const ordersCollectionRef = collection(db, 'orders');
      const q = query(ordersCollectionRef, where('orderedByUid', '==', userId));
      const data = await getDocs(q);
      let fetchedOrders = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      fetchedOrders.sort((a, b) => b.orderNumber.localeCompare(a.orderNumber));
      setOrders(fetchedOrders);
    } catch (err) {
      setError('発注履歴の取得に失敗しました。');
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = '発注履歴 | My Order System';
    const user = auth.currentUser;
    if (!user) {
      setError('ログインしていません。');
      setLoading(false);
      return;
    }
    fetchData(user.uid);
  }, [fetchData]);

  const isOrderStillModifiable = useCallback((order) => {
      const deadline = order.modifiableUntil?.toDate();
      if (!deadline) return false;
      return new Date() < deadline;
  }, []);

  const modifiableOrders = useMemo(() => orders.filter(isOrderStillModifiable), [orders, isOrderStillModifiable]);
  const pastOrders = useMemo(() => orders.filter(order => !isOrderStillModifiable(order)), [orders, isOrderStillModifiable]);

  const indexOfLastPastOrder = pastOrdersCurrentPage * itemsPerPage;
  const indexOfFirstPastOrder = indexOfLastPastOrder - itemsPerPage;
  const currentPastOrders = pastOrders.slice(indexOfFirstPastOrder, indexOfLastPastOrder);
  const totalPastPages = Math.ceil(pastOrders.length / itemsPerPage);
  const paginatePastOrders = (pageNumber) => setPastOrdersCurrentPage(pageNumber);

  const handleCancelOrder = async (orderId, orderNumber) => {
    if (window.confirm(`発注番号: ${formatDisplayOrderNumber(orderNumber)} の注文を本当にキャンセルしますか？\nこの操作は元に戻せません。`)) {
      try {
        await deleteDoc(doc(db, 'orders', orderId));
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        alert('注文をキャンセルしました。');
      } catch (err) {
        setError('注文のキャンセルに失敗しました。');
        console.error("Order cancellation error:", err);
      }
    }
  };

  const renderOrderAccordion = (orderList, isPastOrder = false) => {
    if (orderList.length === 0) {
      return <p className="text-muted">{isPastOrder ? '過去の注文履歴はありません。' : '現在、変更可能な注文はありません。'}</p>;
    }
    return (
      <Accordion>
        {orderList.flatMap(order =>
          order.shippingDestinations.map((dest) => {
            const invoiceDetailsText = order.invoiceDetails || '';
            const statusInfo = statusMap[order.status] || { text: order.status || '新規', bg: 'secondary' };
            const trackingUrl = getTrackingUrl(order.shippingCarrier, dest.trackingNumber);
            return (
              <Accordion.Item eventKey={dest.orderNumber} key={dest.orderNumber}>
                <Accordion.Header>
                  <Container fluid>
                    <Row className="w-100 align-items-center g-2">
                      <Col xs={12} md={6} className="text-center text-md-start">
                        <div>発注No.</div>
                        <span className="fw-bold fs-5">{formatDisplayOrderNumber(dest.orderNumber)}</span>
                        <hr />
                        <div>
                          送り状No.
                          <span>
                            {order.shippingCarrier === 'yupri' && <span className="badge" style={{ backgroundColor: '#CCCC66', color: 'black' }}>ゆうパケット</span>}
                            {order.shippingCarrier === 'istar' && <span className="badge" style={{ backgroundColor: '#FFCC33', color: 'black' }}>福山通運</span>}
                          </span>
                        </div>
                        <span className="fw-bold fs-5">
                          {dest.trackingNumber ? <a href={trackingUrl} target="_blank" rel="noopener noreferrer">{dest.trackingNumber}</a> : 'ー'}
                        </span>
                      </Col>
                      <Col xs={12} md={5} className="text-center text-md-start">
                        <div className="text-muted small">送付先: {dest.recipientName} 様</div>
                        <div><small className="text-muted">発注日時:</small><span>{order.orderedAt?.toDate().toLocaleString('ja-JP')}</span></div>
                        <div><small className="text-muted">出荷予定:</small><strong>{formatShippingDate(order.estimatedShippingDate)}</strong></div>
                      </Col>
                      <Col xs={12} md={1} className="text-center text-md-start">
                        <Badge bg={statusInfo.bg}>{statusInfo.text}</Badge>
                      </Col>
                      <Col xs={12} className='border p-2'>
                        <div className='text-muted small'>送り状に記載する事項:</div>
                        <div>{invoiceDetailsText}</div>
                      </Col>
                    </Row>
                  </Container>
                </Accordion.Header>
                <Accordion.Body>
                  <h5>注文詳細</h5>
                  <Table bordered size="sm">
                    <tbody>
                      <tr><td className="bg-light">送付先会社名</td><td>{dest.shippingCompanyName || '(記載なし)'}</td></tr>
                      <tr><td className="bg-light">支店・部署名</td><td>{`${dest.branchName || ''} ${dest.departmentName || ''}`.trim() || '(記載なし)'}</td></tr>
                      <tr><td className="bg-light">送付先氏名</td><td>{`${dest.recipientName || ''}` || '(記載なし)'}</td></tr>
                      <tr><td className="bg-light">送付先住所</td><td>〒{dest.postalCode} | {dest.address1}{dest.address2}{dest.address3}</td></tr>
                      <tr><td className="bg-light">電話番号</td><td>{dest.phoneNumber || '(記載なし)'}</td></tr>
                    </tbody>
                  </Table>
                  {dest.trackingNumber && (
                    <>
                      <h5 className="mt-3">配送情報</h5>
                      <Table bordered size="sm">
                        <tbody>
                          <tr>
                            <td className="bg-light" style={{ width: '150px' }}>配送業者</td>
                            <td>{order.shippingCarrier === 'yupri' ? '日本郵便' : order.shippingCarrier === 'istar' ? '福山通運' : '不明'}</td>
                          </tr>
                          <tr>
                            <td className="bg-light">お問い合わせ番号</td>
                            <td>
                              {trackingUrl ? <a href={trackingUrl} target="_blank" rel="noopener noreferrer">{dest.trackingNumber}</a> : <span>{dest.trackingNumber}</span>}
                              <Button variant="link" size="sm" onClick={() => navigator.clipboard.writeText(dest.trackingNumber)}><Clipboard /></Button>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </>
                  )}
                  <h5 className="mt-3">商品リスト</h5>
                  <Table striped bordered hover responsive>
                    <thead><tr><th>品番</th><th>数量</th><th>備考</th></tr></thead>
                    <tbody>
                      {[...order.products]
                        .sort((a, b) => (a.partNumber || '').localeCompare(b.partNumber || ''))
                        .map((product, pIndex) => (
                          <tr key={pIndex}>
                            <td>{product.partNumber}</td>
                            <td>{product.quantity}</td>
                            <td>{product.remarks}</td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                  {!isPastOrder && (
                    <>
                      <hr />
                      {/* 【変更点】deliveryDeadlineTimeをpropsとして渡す */}
                      <OrderActions
                        order={order}
                        orderChangeTimeLimit={orderChangeTimeLimit}
                        orderChangeDeadlineTime={orderChangeDeadlineTime}
                        deliveryDeadlineTime={deliveryDeadlineTime}
                        onCancel={handleCancelOrder}
                      />
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            );
          })
        )}
      </Accordion>
    );
  };

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header className="p-3 d-flex flex-column flex-md-row justify-content-md-between align-items-md-end">
            <h2 className="mb-2 mb-md-0">発注履歴</h2>
            <Link to="/mainmenu"><small>メインメニューに戻る</small></Link>
          </Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            {loading ? <div className="text-center"><Spinner animation="border" /></div> : (
              <>
                <section className="mb-5">
                  <h3>新規注文履歴 (変更・キャンセル可能)</h3>
                  {renderOrderAccordion(modifiableOrders)}
                </section>
                <hr />
                <section>
                  <h3>過去の注文履歴</h3>
                  {renderOrderAccordion(currentPastOrders, true)}
                  {totalPastPages > 1 && (
                    <Pagination className="justify-content-center mt-3">
                      <Pagination.First onClick={() => paginatePastOrders(1)} disabled={pastOrdersCurrentPage === 1} />
                      <Pagination.Prev onClick={() => paginatePastOrders(pastOrdersCurrentPage - 1)} disabled={pastOrdersCurrentPage === 1} />
                      {[...Array(totalPastPages).keys()].map(number => (
                        <Pagination.Item key={number + 1} active={number + 1 === pastOrdersCurrentPage} onClick={() => paginatePastOrders(number + 1)}>
                          {number + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next onClick={() => paginatePastOrders(pastOrdersCurrentPage + 1)} disabled={pastOrdersCurrentPage === totalPastPages} />
                      <Pagination.Last onClick={() => paginatePastOrders(totalPastPages)} disabled={pastOrdersCurrentPage === totalPastPages} />
                    </Pagination>
                  )}
                </section>
              </>
            )}
            <hr className="my-4" />
            <Link to="/mainmenu">メインメニューに戻る</Link>
          </Card.Body>
        </Card>
      </Container>
      <ScrollToTopButton />
    </main>
  );
}

export default OrderHistory;
